// ─────────────────────────────────────────────────────────────────────────────
// Spotlight, CD's dark hero ("this month's biggest mover"). Populated when the
// band has stats (a top mover / any followers); otherwise CD's empty hero with
// the hidden delta + "-" statiles. Data: DashboardSpotlight + the #1 BiggestMover
// (for the headline platform + its delta).
// ─────────────────────────────────────────────────────────────────────────────

import type { CSSProperties, JSX } from 'react';
import type { DashboardSpotlight, BiggestMover } from '../../lib';
import { fmt, pctLabel } from './format';
import { ArrowUpRight } from './glyphs';

export interface SpotlightProps {
  readonly stats: DashboardSpotlight | null;
  /** The #1 ranked mover (drives the headline platform + delta), or null. */
  readonly topMover: BiggestMover | null;
  /**
   * Live spotlight background photo (base64 data URL), or '' when none.
   * Mirrors applySpotlightPhoto() (dashboard-wiring.js:2100-2156): when present,
   * paint it as an inline background-image under the crimson gradient; when '',
   * leave CD's .spotlight__bg gradient (the empty state).
   */
  readonly photoUrl?: string;
}

/**
 * Build the inline backgroundImage exactly as applySpotlightPhoto()
 * (dashboard-wiring.js:2134-2143): the crimson gradient over the live photo.
 * `acSafe.cssUrl()` in the legacy app sanitizes the url to block CSS-injection
 * through user-supplied photo paths; we mirror that by dropping any character
 * that could break out of the url('…') wrapper (quotes, parens, whitespace,
 * backslash). Our photos are self-hosted base64 data URLs, which contain none.
 */
function spotlightBgImage(photoUrl: string): string {
  const safe = photoUrl.replace(/["'()\\\s]/g, '');
  return (
    'linear-gradient(135deg,' +
    'rgba(26,14,13,0.92) 0%,' +
    'rgba(45,21,24,0.78) 45%,' +
    'rgba(139,26,43,0.55) 100%),' +
    "url('" +
    safe +
    "')"
  );
}

/** Inline style mirroring applySpotlightPhoto()'s cover/center/no-repeat. */
function spotlightStyle(photoUrl: string): CSSProperties | undefined {
  if (!photoUrl) return undefined;
  return {
    backgroundImage: spotlightBgImage(photoUrl),
    backgroundSize: 'cover, cover',
    backgroundPosition: 'center, center',
    backgroundRepeat: 'no-repeat, no-repeat',
  };
}

export function Spotlight({ stats, topMover, photoUrl = '' }: SpotlightProps): JSX.Element {
  // Populated when there's something to spotlight: a ranked mover OR followers.
  const hasData = !!stats && (stats.totalFollowers > 0 || !!topMover);

  // When a live press photo exists, apply it inline (legacy applies large base64
  // directly, NOT via a CSS var) + add the .spotlight--has-photo hook.
  const hasPhoto = !!photoUrl;
  const cardClass = 'spotlight' + (hasPhoto ? ' spotlight--has-photo' : '');
  const cardStyle = spotlightStyle(photoUrl);

  if (!hasData) {
    // ── EMPTY (dashboard-empty.html spotlight) ──
    return (
      <article className={cardClass} id="spotlight-card">
        <div className="spotlight__bg" aria-hidden="true" style={cardStyle}></div>
        <header className="spotlight__head">
          <div>
            <p className="spotlight__eyebrow">★ This month's spotlight</p>
            <h2 className="spotlight__title disp">Your monthly spotlight</h2>
            <p className="spotlight__sub">
              Once your platforms are connected, your biggest moves each month show up here.
            </p>
          </div>
          <span className="delta" hidden>
           ,
          </span>
        </header>
        <div className="spotlight__stats">
          <div className="statile">
            <p className="statile__label">Total followers</p>
            <p className="statile__value">-</p>
          </div>
          <div className="statile">
            <p className="statile__label">Views (30D)</p>
            <p className="statile__value">-</p>
          </div>
          <div className="statile">
            <p className="statile__label">Top mover</p>
            <p className="statile__value statile__value--sm">-</p>
          </div>
        </div>
      </article>
    );
  }

  // ── POPULATED ──
  const moverLabel = topMover?.label || '';
  const delta = topMover?.delta ?? null;
  const deltaUp = delta == null || delta >= 0;
  // Only claim a platform is "trending" / "led the pack" when we have a real
  // 30-day delta. With a value-only top mover (no trend history yet) keep the
  // headline generic but still surface the platform in the Top-mover tile.
  const hasGrowth = !!moverLabel && delta != null;
  const title = hasGrowth ? moverLabel + ' is trending this month' : 'Your audience is growing';

  return (
    <article className={cardClass} id="spotlight-card" style={cardStyle}>
      <div className="spotlight__bg" aria-hidden="true"></div>

      <header className="spotlight__head">
        <div>
          <p className="spotlight__eyebrow">★ This month's spotlight</p>
          <h2 className="spotlight__title disp">{title}</h2>
          <p className="spotlight__sub">
            {hasGrowth
              ? 'Your audience grew across every channel, ' + moverLabel + ' led the pack with the biggest 30-day lift.'
              : 'Your audience across every channel, in one place.'}
          </p>
        </div>
        {delta != null && (
          <span className={'delta ' + (deltaUp ? 'delta--up' : 'delta--down')}>
            <ArrowUpRight />
            {pctLabel(delta)}
          </span>
        )}
      </header>

      <div className="spotlight__stats">
        <div className="statile">
          <p className="statile__label">Total followers</p>
          <p className="statile__value">{stats!.totalFollowers > 0 ? fmt(stats!.totalFollowers) : '-'}</p>
        </div>
        <div className="statile">
          <p className="statile__label">Views (30D)</p>
          <p className="statile__value">
            {stats!.monthlyListeners > 0 ? fmt(stats!.monthlyListeners) : '-'}
          </p>
        </div>
        <div className="statile">
          <p className="statile__label">Top mover</p>
          <p className="statile__value statile__value--sm">
            {moverLabel || '-'}
            {delta != null && <span className="statile__pos">{pctLabel(delta)}</span>}
          </p>
        </div>
      </div>
    </article>
  );
}
